/* eslint-disable max-lines */
import React, { useCallback, useState, useContext } from "react";
import { Form, Formik, FormikConfig, useField } from "formik";
import { Grid, RadioGroup, FormControl, FormLabel, FormControlLabel, Radio, FormHelperText, InputBaseComponentProps, Typography, InputAdornment, IconButton } from "@material-ui/core";
import * as yup from "yup";
import { useMutation } from "@apollo/client";
import classNames from "classnames";
import { Alert } from "@material-ui/lab";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import { Card, Button } from "components/haven-design";
import { InputWithErrorLabel } from "components/haven-design/form/inputs";
import { TosCheckbox } from "components/haven-design/form/checkbox";
import { CreateApplicantAccount, CreateApplicantAccountVariables, UserLoginPreflight, UserLoginPreflightVariables } from "types/gql-types";
import { initializeClientSignup } from "services/analytics";
import { apolloClient } from "data/graphql";
import { useSnackbar } from "components/haven-design/snackbar";
import logger from "services/logger";
import { CREATE_NEW_USER, SIGN_IN_ATTEMPT } from "../query";
import { Page } from "../type";
import { passwordValidations } from "../utils";
import LoginModalContext from "../context/modal";
import styles from "./Login.module.css";
import { LoginCardSubtitle, LoginCardTitle, LoneGrid, TextMaskCustom } from "./shared-components";
import { loginStyles } from "./styles";

const validateIdentifierAvailable = async (identifier: string | undefined) => {
  if (identifier) {
    const usernamePreflight = apolloClient.mutate<UserLoginPreflight, UserLoginPreflightVariables>({
      mutation: SIGN_IN_ATTEMPT,
      variables: { username: identifier },
    });

    const result = await usernamePreflight;
    const { userLoginPreflight } = result.data || { userLoginPreflight: null };
    return userLoginPreflight?.login === null;
  }
  return true;
};

const schema = yup.object({
  firstName: yup.string()
    .matches(/^\s*[A-z'-\s]+\d{0}\s*$/, "First Name can only include letters, apostrophes or dashes")
    .required(),

  lastName: yup.string()
    .matches(/^\s*[A-z'-\s]+\d{0}\s*$/, "Last Name can only include letters, apostrophes or dashes")
    .required(),

  loginMethod: yup.string().oneOf(["email", "phone"]),

  email: yup.string().when("loginMethod", {
    is: "email",
    then: yup.string().required("A valid email address is required."),
  }).email()
    // eslint-disable-next-line no-return-await
    .test("emailAvailable", "Account already exists with this email", async (value) => await validateIdentifierAvailable(value)),

  phone: yup.string().when("loginMethod", {
    is: "phone",
    then: yup.string().required("A valid US mobile phone number is required."),
  }).validPhoneNumber()
    // eslint-disable-next-line no-return-await
    .test("phoneAvailable", "Account already exists with this mobile phone", async (value) => await validateIdentifierAvailable(value)),

  password: yup
    .mixed()
    .when("loginMethod", { is: "email", then: yup.string().validPassword().required() }),

  passwordConfirm: yup
    .mixed()
    .when("loginMethod", { is: "email", then: yup.string().validPassword().required() })
    .test("equalToPassword", "The passwords given must match.", (value, context) => context.parent.password === value),

  tos: yup
    .bool()
    .oneOf(
      [true],
      "You must agree to the terms of use and privacy policy in order to create an account.",
    ),
});

interface FormValues {
  firstName: string;
  lastName: string;
  loginMethod: "email" | "phone";
  email: string;
  phone: string;
  password: string;
  passwordConfirm: string;
  tos: boolean;
}

type LoginMethodSelectorProps = { name: string };
const LoginMethodSelector: React.FC<LoginMethodSelectorProps> = (props) => {
  const { name, ...radioGroupProps } = props;
  const [field, meta] = useField(name);

  return (
    <FormControl>
      <FormLabel>
        How do you want to login?
      </FormLabel>
      <RadioGroup
        row
        {...field}
        {...radioGroupProps}
      >
        <FormControlLabel
          value="email"
          label="Email"
          control={<Radio />}
        />
        <FormControlLabel
          value="phone"
          label="Mobile Phone"
          control={<Radio />}
        />
        { meta.touched && meta.error && (<FormHelperText>{meta.error}</FormHelperText>)}
      </RadioGroup>
    </FormControl>
  );
};

/* eslint-disable max-lines-per-function */
const SignUpCard: Page<"create"> = (props) => {
  const [pass, setPass] = useState<string>("");
  const [passwordErrors, setPasswordErrors] =
    useState(passwordValidations.filter(({ check }) => check(pass)));
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState<boolean>(false);

  const [createNewUser, createResult] = useMutation<
  CreateApplicantAccount,
  CreateApplicantAccountVariables
  >(CREATE_NEW_USER);

  const { inModal } = useContext(LoginModalContext);
  const { openSnackbar } = useSnackbar();

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleTogglePasswordConfirmVisibility = () => {
    setShowPasswordConfirm(!showPasswordConfirm);
  };

  const validatePassword = (password: string) => {
    const pendingErrors = passwordValidations.filter(({ check }) => check(password));
    setPasswordErrors(pendingErrors);
  };

  const renderPasswordValidations = () => {
    const errorsList = passwordErrors.map((error, idx) => <li key={idx}>{error.shortMessage}</li>);

    if (errorsList.length < 1) return (<></>);

    return (
      <Grid container spacing={2} direction="column" alignItems="flex-start" justifyContent="flex-start" item >
        <ul style={loginStyles.passwordRequirements}>
          {errorsList}
        </ul>
      </Grid>
    );
  };

  const initialValues = {
    firstName: "",
    lastName: "",
    loginMethod: "email" as FormValues["loginMethod"],
    email: "",
    phone: "",
    password: "",
    passwordConfirm: "",
    tos: false,
  };

  const onSubmit: FormikConfig<FormValues>["onSubmit"] = useCallback((values: FormValues) => {
    const variables = {
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      email: values.email,
      phone: values.phone,
      password: values.password,
      roles: ["client"],
    };

    const request = createNewUser({ variables: { data: variables } });

    request.then((result) => {
      const id = result.data?.createUser?.id;
      // eslint-disable-next-line no-undefined
      if (id === null || id === undefined) {
        return;
      }

      initializeClientSignup({
        userId: id,
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phone: values.phone,
      });

      openSnackbar({ message: "Account successfully created", status: "success" });

      switch (values.loginMethod) {
        case "email": {
          props.onNavigate({
            page: "wait-to-verify",
            props: { username: values.email },
          });
          break;
        }
        case "phone": {
          props.onNavigate({
            page: "login",
            props: {},
          });
          break;
        }
        default: {
          break;
        }
      }
    })
      .catch((err) => {
        logger.sendError(err);
        openSnackbar({ message: "Account could not be created", status: "error" });
      });
  }, []);

  return (
    <Formik<FormValues>
      enableReinitialize
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={onSubmit}
    >
      {(form) => (
        <Form className={styles.form}>
          <Card className={classNames(styles.loginCard, { [styles.borderlessCard]: inModal })}>
            <Grid
              container
              spacing={1}
              direction="column"
              alignItems="center"
              justifyContent="center"
            >
              <Grid item>
                <LoginCardTitle title="Find Affordable Housing" />
                <LoginCardSubtitle>
                  Join Haven Connect to apply to affordable housing opportunities and waitlists.
                </LoginCardSubtitle>
              </Grid>
              <Grid item>
                <InputWithErrorLabel
                  label="First name"
                  name="firstName"
                  type="text"
                  style={loginStyles.loginInput}
                  autoFocus
                />
              </Grid>
              <Grid item>
                <InputWithErrorLabel
                  label="Last name"
                  name="lastName"
                  type="text"
                  style={loginStyles.loginInput}
                />
              </Grid>
              <Grid item>
                <InputWithErrorLabel
                  label="Email address"
                  name="email"
                  type="text"
                  style={loginStyles.loginInput}
                />
              </Grid>
              <Grid item>
                <InputWithErrorLabel
                  label="Mobile Phone"
                  name="phone"
                  type="text"
                  InputProps={{
                    inputComponent: TextMaskCustom as React.ElementType<InputBaseComponentProps>,
                  }}
                  style={loginStyles.loginInput}
                  placeholder="(999) 123-4567"
                />
              </Grid>
              { form.values.loginMethod === "email" &&
                <>
                  <Grid item>
                    <InputWithErrorLabel
                      label="Choose a password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      style={loginStyles.loginInput}
                      value={pass}
                      onChange={(e) => {
                        form.setFieldValue("password", e.target.value, true);
                        setPass(e.target.value);
                        validatePassword(e.target.value);
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleTogglePasswordVisibility}
                              edge="end"
                              size="small"
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  {renderPasswordValidations()}
                  <Grid item>
                    <InputWithErrorLabel
                      label="Repeat password"
                      name="passwordConfirm"
                      type={showPasswordConfirm ? "text" : "password"}
                      style={loginStyles.loginInput}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password confirmation visibility"
                              onClick={handleTogglePasswordConfirmVisibility}
                              edge="end"
                              size="small"
                            >
                              {showPasswordConfirm ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </>
              }
              <Grid item>
                <LoginMethodSelector name="loginMethod" />
              </Grid>
              <Grid item>
                <TosCheckbox
                  name="tos"
                />
              </Grid>
              <Grid item>
                <Alert icon={false} severity="info">
                  <Typography variant="caption">
                    By proceeding, you are consenting to receive calls or SMS messages including
                    by automated dialer, from Haven Connect and its affiliates to the number
                    you provide. You understand that you may opt out by texting &quot;STOP&quot; to
                    the Haven Connect number that messaged you.
                  </Typography>
                </Alert>
              </Grid>
              <Grid item>
                <Button
                  id="create-account"
                  type="submit"
                  color="primary"
                  size="L"
                  className={styles.loginButton}
                  disabled={
                    Object.keys(form.touched).length === 0 ||
                    !form.isValid ||
                    createResult.loading
                  }
                >
                  Create Your New Account
                </Button>
                {props.children}
                <LoneGrid>
                  {!createResult.loading && createResult.error && (
                    <FormHelperText error style={{ marginTop: 24 }}>
                      {createResult.error.graphQLErrors[0].message}
                    </FormHelperText>
                  )}
                </LoneGrid>
              </Grid>
            </Grid>
          </Card>
        </Form>
      )}
    </Formik>
  );
};
/* eslint-enable max-lines-per-function */

export default SignUpCard;
